/* eslint-disable react/prop-types */
/**
 * LazyMount —— 滚动到视口附近才挂载子树
 * ------------------------------------------------------------------
 * 用途：单页平铺（锚点模式）后，一屏内会并排很多「重块」（图表、自取数的
 * 表格）。若全部同时挂载，会出现两个问题：
 * 1. **请求一次全发** —— 自取数的块（如会员打卡、看得见活动）会立刻发请求，
 *    而用户可能根本不往下滚；
 * 2. **白渲染** —— 图表（canvas）初始化开销不小，滚动前渲染等于浪费。
 *
 * 判据：本块距视口 400px 以内（约提前一屏）时挂载。
 *
 * 三条设计约束（都是踩过的）：
 * 1. **只挂不卸** —— 一旦挂载就 `disconnect`，滚动来回不再卸载：
 *    卸载会丢组件内部状态（筛选、分页）并重复触发请求。
 * 2. **占位必须有高度** —— 未挂载时若高度为 0，承载它的 `section` 会塌陷，
 *    导致锚点点击落到错误位置、滚动联动的高低判定全错。所以占位给
 *    `min-height`（见 less）。
 * 3. **降级要安全** —— 环境没有 `IntersectionObserver` 时直接挂载内容，
 *    宁可多渲染也不能让用户看不到。
 *
 * @param placeholder 未挂载时的占位内容（默认一个浅色块）
 */
import React, { useEffect, useRef, useState } from 'react';

import styles from './LazyMount.less';

/** 提前挂载的距离：约一屏，避免滚动时看到占位闪一下 */
const PRELOAD_MARGIN = '400px 0px';

export const LazyMount = (props) => {
  const { children, className, placeholder } = props;
  const hostRef = useRef(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    if (mounted) return undefined;

    const host = hostRef.current;
    /* 降级：没有观察器（或拿不到节点）就直接挂载，内容优先 */
    if (!host || typeof IntersectionObserver === 'undefined') {
      setMounted(true);
      return undefined;
    }

    const io = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        setMounted(true);
        /* 只挂不卸：挂载后立刻停止观察 */
        io.disconnect();
      },
      { rootMargin: PRELOAD_MARGIN },
    );
    io.observe(host);
    return () => io.disconnect();
  }, [mounted]);

  return (
    <div ref={hostRef} className={className}>
      {mounted ? (
        children
      ) : (
        <div className={styles.placeholder} aria-hidden="true">
          {placeholder}
        </div>
      )}
    </div>
  );
};

export default LazyMount;
